import { banco } from '../db/banco'
import { novoRegistroBase, agoraISO } from '../db/tipos'
import type { Cliente, Visita } from '../db/tipos'
import { gravarLocalEEnfileirar } from '../sync/outbox'
import { dataISOLocal } from '../pedidos/precos'
import { retornosDoDia, pendenciasDeRetorno, dataDoDiaDaSemana, type Retorno } from './retorno'

export function diasDesde(dataISO: string | null, hoje: Date): number | null {
  if (!dataISO) return null
  const [ano, mes, dia] = dataISO.split('-').map(Number)
  const ms =
    new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()).getTime() -
    new Date(ano, mes - 1, dia).getTime()
  return Math.round(ms / 86_400_000)
}

export async function clientesDoDia(diaSemana: number): Promise<Cliente[]> {
  return banco.clientes
    .where('dia_visita')
    .equals(diaSemana)
    .filter((c) => c.apagado_em === null && c.status === 'ativo')
    .sortBy('razao_social')
}

export async function ultimaCompraPorCliente(): Promise<Map<string, string>> {
  const pedidos = await banco.pedidos
    .filter((p) => p.apagado_em === null && p.status === 'fechado')
    .toArray()
  const mapa = new Map<string, string>()
  for (const pedido of pedidos) {
    const atual = mapa.get(pedido.cliente_id)
    if (!atual || pedido.data > atual) mapa.set(pedido.cliente_id, pedido.data)
  }
  return mapa
}

// Chave por cliente, e nao lista: a regra e UMA visita por cliente por dia.
export async function visitasDeHoje(): Promise<Map<string, Visita>> {
  const hoje = dataISOLocal(new Date())
  // ⚠ `data_hora` e gravado por agoraISO(), que e UTC. Comparar os 10 primeiros
  // caracteres dele com uma data LOCAL faz os dois discordarem entre 21h e
  // meia-noite de Brasilia -- e a visita que ela acabou de registrar sumia da
  // agenda do dia, virava visita duplicada e perdia o elo com o pedido. Sao 3
  // horas por dia, todo dia, e o app e usado na rua.
  //
  // O dia da visita e o do RELOGIO DELA. Converter o instante gravado para o
  // dia local resolve o dado que ja existe tambem, sem migration.
  const visitas = await banco.visitas
    .filter((v) => v.apagado_em === null && dataISOLocal(new Date(v.data_hora)) === hoje)
    .toArray()
  const mapa = new Map<string, Visita>()
  for (const visita of visitas) mapa.set(visita.cliente_id, visita)
  return mapa
}

export async function registrarVisita(
  clienteId: string,
  resultado: 'com_pedido' | 'sem_pedido',
  motivo: Visita['motivo_sem_pedido'],
  observacao: string | null,
  // Quem CHAMA sabendo qual pedido gerou a venda entrega o id aqui, em vez de
  // deixar a busca por data adivinhar. E o caso do fecharPedido: um pedido
  // aberto ontem e fechado hoje nao aparece na busca por "data == hoje", e a
  // visita gravaria pedido_id null em SILENCIO -- verde na tela, elo perdido no
  // dado que os relatorios das fases 7 e 8 vao ler.
  pedidoDaVenda: string | null = null,
): Promise<void> {
  const existente = (await visitasDeHoje()).get(clienteId)

  // "Vendi" ganha de "nao vendi", e nunca o contrario: um check-in manual
  // esquecido nao pode apagar a prova de que o pedido existe.
  if (existente?.resultado === 'com_pedido' && resultado === 'sem_pedido') return

  let pedidoId: string | null = existente?.pedido_id ?? null
  if (resultado === 'com_pedido') {
    if (pedidoDaVenda) {
      pedidoId = pedidoDaVenda
    } else {
      // Sem o id em maos, o palpite util: o ultimo pedido do cliente hoje.
      const hoje = dataISOLocal(new Date())
      const deHoje = await banco.pedidos
        .where('cliente_id')
        .equals(clienteId)
        .filter((p) => p.apagado_em === null && p.data === hoje && p.status !== 'cancelado')
        .sortBy('criado_em')
      pedidoId = deHoje[deHoje.length - 1]?.id ?? null
    }
  }

  // Atualiza a visita do dia em vez de criar outra -- e por isso o ...existente
  // vem primeiro: preserva id e criado_em, entao o servidor faz upsert e nao
  // acumula duas linhas para a mesma visita.
  await gravarLocalEEnfileirar('visitas', {
    ...(existente ?? novoRegistroBase()),
    cliente_id: clienteId,
    data_hora: existente?.data_hora ?? agoraISO(),
    resultado,
    pedido_id: pedidoId,
    motivo_sem_pedido: resultado === 'sem_pedido' ? motivo : null,
    observacao,
    atualizado_em: agoraISO(),
  })

  // Decisao dela 06/10: visitar ANTES da data marcada apaga o retorno marcado
  // (volta a valer o "a cada X dias", contado desta visita). So na visita NOVA
  // do dia: o "Nao comprou + data" grava a visita e remarca logo depois. O
  // "desfazer" da visita nao devolve a data apagada.
  if (!existente) {
    const c = await banco.clientes.get(clienteId)
    if (c && (c.retorno_marcado ?? '') > dataISOLocal(new Date())) {
      await gravarLocalEEnfileirar('clientes', { ...c, retorno_marcado: null, atualizado_em: agoraISO() })
    }
  }
}

export async function desfazerVisita(clienteId: string): Promise<void> {
  const existente = (await visitasDeHoje()).get(clienteId)
  if (!existente) return
  await gravarLocalEEnfileirar('visitas', { ...existente, apagado_em: agoraISO() })
}

export async function definirDiaDeVisita(clienteId: string, dia: number | null): Promise<void> {
  const cliente = await banco.clientes.get(clienteId)
  if (!cliente) return
  await gravarLocalEEnfileirar('clientes', {
    ...cliente,
    dia_visita: dia,
    atualizado_em: agoraISO(),
  })
}

// ▶ Ciclo 2B (spec 2026-10-06). Dia da visita = dia do RELOGIO dela, como em
// visitasDeHoje: data_hora e UTC.
// `desdeISO` (instante UTC, opcional): o sininho le so a janela dele em vez de
// varrer todas as visitas a cada gravacao (revisao final 2C).
export async function datasDeVisitaPorCliente(desdeISO?: string): Promise<Map<string, string[]>> {
  const mapa = new Map<string, string[]>()
  const fonte = desdeISO ? banco.visitas.where('data_hora').aboveOrEqual(desdeISO) : banco.visitas.toCollection()
  const visitas = await fonte.filter((v) => v.apagado_em === null).toArray()
  for (const v of visitas) {
    const dia = dataISOLocal(new Date(v.data_hora))
    const lista = mapa.get(v.cliente_id)
    if (lista) lista.push(dia)
    else mapa.set(v.cliente_id, [dia])
  }
  return mapa
}

export async function rotaDoDia(
  diaSemana: number, agora: Date,
): Promise<{ retornos: Retorno[]; doDia: Cliente[] }> {
  const hoje = dataISOLocal(agora)
  const data = dataDoDiaDaSemana(hoje, agora.getDay(), diaSemana)
  const [clientes, datas, doDiaBruto] = await Promise.all([
    banco.clientes.toArray(), datasDeVisitaPorCliente(), clientesDoDia(diaSemana),
  ])
  const retornos = retornosDoDia(clientes, datas, data, hoje)
  const noTopo = new Set(retornos.map((r) => r.cliente.id))
  // Revisao final: "Nao comprou + data" muda o vencimento para o futuro e o
  // cliente sumiria do bloco na hora, com o selo verde e o ✕ de desfazer. Na
  // rota de HOJE, quem foi visitado hoje com data marcada futura e NAO e do dia
  // da semana so podia estar no bloco Retorno -- fica nele ate amanha.
  if (data === hoje) {
    const daSemana = new Set(doDiaBruto.map((c) => c.id))
    for (const c of clientes) {
      if (noTopo.has(c.id) || daSemana.has(c.id) || c.apagado_em !== null || c.status !== 'ativo') continue
      if (!(datas.get(c.id) ?? []).includes(hoje) || !((c.retorno_marcado ?? '') > hoje)) continue
      retornos.push({ cliente: c, vencimento: c.retorno_marcado!, diasDeAtraso: 0 })
      noTopo.add(c.id)
    }
  }
  return { retornos, doDia: doDiaBruto.filter((c) => !noTopo.has(c.id)) }
}

export async function pendenciasDeRetornoDeHoje(agora: Date): Promise<Retorno[]> {
  const [clientes, datas] = await Promise.all([banco.clientes.toArray(), datasDeVisitaPorCliente()])
  return pendenciasDeRetorno(clientes, datas, dataISOLocal(agora))
}

// "Nao visitei / remarquei" e a data do "Nao comprou": so a data, sem visita.
export async function remarcarRetorno(clienteId: string, data: string): Promise<void> {
  const c = await banco.clientes.get(clienteId)
  if (!c) return
  await gravarLocalEEnfileirar('clientes', { ...c, retorno_marcado: data, atualizado_em: agoraISO() })
}

export async function excluirAvisoDeRetorno(clienteId: string, vencimento: string): Promise<void> {
  const c = await banco.clientes.get(clienteId)
  if (!c) return
  await gravarLocalEEnfileirar('clientes', { ...c, retorno_aviso_excluido: vencimento, atualizado_em: agoraISO() })
}
